/* Shared Buitn Planten header with announcement strip, matching announcement icons, free-shipping message, search row, catalogue navigation, deduplicated Klantenservice dropdown, and caret-free Inspiratie link.
   Updated: Review score now auto-calculated from published reviews via get_review_stats() instead of manual site_settings. */
import { useEffect, useState } from 'react';
import { ChevronDown, Heart, Leaf, Menu, X, Building2, Star } from 'lucide-react';
import AssortimentMegaMenu from './AssortimentMegaMenu';
import CartButton from './cart/CartButton';
import FavoritesDrawer from './favorites/FavoritesDrawer';
import SearchBar from './SearchBar';
import { useFavorites } from '@/contexts/FavoritesContext';
import { supabase } from '@/lib/supabase';

interface KlantenservicePage {
  slug: string;
  title: string;
  display_order: number;
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isKlantenserviceOpen, setIsKlantenserviceOpen] = useState(false);
  const [klantenservicePages, setKlantenservicePages] = useState<KlantenservicePage[]>([]);
  const [categoriesWithProducts, setCategoriesWithProducts] = useState<Set<string>>(new Set());
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [reviewScore, setReviewScore] = useState<number>(0);
  const { favoriteCount } = useFavorites();

  useEffect(() => {
    const loadNavigationData = async () => {
      const [{ data: pages }, { data: products }, { data: statsData }] = await Promise.all([
        supabase.from('klantenservice_pages').select('slug, title, display_order').eq('active', true).order('display_order'),
        supabase.from('products').select('product_categories!inner(slug)').eq('active', true).eq('product_categories.active', true),
        supabase.rpc('get_review_stats'),
      ]);

      if (statsData && statsData.length > 0) setReviewScore(parseFloat(statsData[0].avg_score) || 0);
      if (pages) setKlantenservicePages(pages);
      if (products) {
        setCategoriesWithProducts(new Set(products.flatMap((item: any) => item.product_categories?.slug ? [item.product_categories.slug] : [])));
      }
    };

    loadNavigationData();
  }, []);

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const navItems = [
    { name: 'Haagplanten', href: '/haagplanten', categorySlug: 'haagplanten' },
    { name: 'Bomen', href: '/bomen', categorySlug: 'bomen' },
    { name: 'Tuinplanten', href: '/tuinplanten', categorySlug: 'tuinplanten' },
    { name: 'Aanplant & verzorging', href: '/aanplant-en-verzorging', categorySlug: null },
  ];
  const klantenserviceItems = [
    { slug: 'aanplantservice', title: 'Aanplantservice' },
    ...klantenservicePages,
  ].filter((item, index, items) => items.findIndex((candidate) => candidate.slug === item.slug) === index);

  const closeMenus = () => {
    setIsMenuOpen(false);
    setIsMegaMenuOpen(false);
    setIsKlantenserviceOpen(false);
  };

  const renderNavItem = (item: typeof navItems[number]) => {
    const isActive = ['/assortiment', '/haagplanten', '/bomen', '/tuinplanten', '/aanplant-en-verzorging'].includes(currentPath) && currentPath.includes(item.href);
    const canOpenMegaMenu = item.categorySlug && categoriesWithProducts.has(item.categorySlug);

    return (
      <div
        key={item.name}
        className="relative h-full"
        onMouseEnter={() => {
          if (canOpenMegaMenu) {
            setActiveCategory(item.categorySlug);
            setIsMegaMenuOpen(true);
          }
        }}
        onMouseLeave={() => setIsMegaMenuOpen(false)}
      >
        <a
          href={item.href}
          onClick={closeMenus}
          className={`relative flex h-full items-center px-5 text-[14px] tracking-[-0.01em] transition-colors ${isActive ? 'font-semibold text-[#1e2b22]' : 'text-[#1a1a1a] hover:text-[#1e2b22]'}`}
        >
          {item.name}
          {canOpenMegaMenu && <ChevronDown className={`ml-2 h-3.5 w-3.5 text-[#5a6552] transition-transform ${isMegaMenuOpen && activeCategory === item.categorySlug ? 'rotate-180' : ''}`} strokeWidth={1.7} aria-hidden="true" />}
          <span className={`absolute inset-x-5 bottom-0 h-[2px] origin-left bg-[#1e2b22] transition-transform ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
        </a>
      </div>
    );
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 bg-[#fcfaf6] text-[#1a1a1a] shadow-[0_1px_0_rgba(40,45,40,0.08)]">
        <div className="overflow-hidden bg-buitn-bosgroen py-2.5 text-xs text-white/80 sm:text-sm">
          <div className="announcement-marquee mx-auto flex w-max whitespace-nowrap lg:w-auto lg:justify-center">
            <div className="flex shrink-0 items-center gap-4 pr-4 sm:gap-6 sm:pr-6 lg:pr-0">
              <span className="tracking-wide">Gratis verzending vanaf €795,-</span>
              <img src="/assets/laurier-white.webp" alt="" aria-hidden="true" className="h-5 w-5 shrink-0 object-contain opacity-70" />
              <span className="font-medium tracking-wide text-white">20% korting bij afhalen met code AFHALEN20</span>
            </div>
            <div className="flex shrink-0 items-center gap-4 pr-4 sm:gap-6 sm:pr-6 lg:hidden" aria-hidden="true">
              <Leaf className="h-3.5 w-3.5 flex-shrink-0 text-white/60" strokeWidth={1.5} />
              <span className="tracking-wide">Voor een complete tuin</span>
              <img src="/assets/laurier-white.webp" alt="" aria-hidden="true" className="h-5 w-5 shrink-0 object-contain opacity-70" />
              <span className="tracking-wide">Kwaliteit die seizoen na seizoen blijft</span>
              <img src="/assets/laurier-white.webp" alt="" aria-hidden="true" className="h-5 w-5 shrink-0 object-contain opacity-70" />
              <span className="tracking-wide">Gratis verzending vanaf €795,-</span>
              <img src="/assets/laurier-white.webp" alt="" aria-hidden="true" className="h-5 w-5 shrink-0 object-contain opacity-70" />
              <span className="font-medium tracking-wide text-white">20% korting bij afhalen met code AFHALEN20</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 mx-auto flex h-[86px] max-w-[1240px] items-center gap-8 px-5 lg:px-8">
          <a href="/" className="flex shrink-0 items-center" aria-label="Buitn Planten home">
            <img src="/assets/logos/buitnplanten-logo-green.webp" alt="Buitn Planten" className="h-[48px] w-auto" />
          </a>

          <a href="/reviews" className="hidden shrink-0 flex-col items-center gap-0.5 sm:flex" aria-label={`Beoordeeld met een ${reviewScore.toFixed(1).replace('.', ',')} — bekijk reviews`}>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`h-3.5 w-3.5 ${s <= Math.round(reviewScore) ? 'fill-amber-400 text-amber-400' : 'fill-[#e7e1d6] text-[#e7e1d6]'}`} />
              ))}
            </div>
            <span className="text-[11px] font-medium leading-tight text-[#5a6552]">Beoordeeld met een {reviewScore.toFixed(1).replace('.', ',')}</span>
          </a>

          <SearchBar variant="desktop" />

          <div className="ml-auto flex items-center gap-5 lg:gap-7">
            <a href="/zakelijk/inloggen" className="group relative hidden flex-col items-center gap-1 text-[#1a1a1a] hover:text-[#1e2b22] transition-colors sm:flex" aria-label="Zakelijke inlog">
              <span className="relative">
                <Building2 className="h-[23px] w-[23px]" strokeWidth={1.35} />
              </span>
              <span className="text-[11px]">Zakelijk</span>
            </a>
            <button type="button" onClick={() => setIsFavoritesOpen(true)} className="group relative hidden flex-col items-center gap-1 text-[#1a1a1a] hover:text-[#1e2b22] transition-colors sm:flex" aria-label="Favorieten">
              <span className="relative">
                <Heart className="h-[23px] w-[23px]" strokeWidth={1.35} />
                {favoriteCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-jv-accent px-1 text-[10px] font-bold text-white">
                    {favoriteCount}
                  </span>
                )}
              </span>
              <span className="text-[11px]">Favorieten</span>
            </button>
            <div className="flex flex-col items-center gap-1 text-[#1a1a1a]">
              <CartButton />
              <span className="-mt-1 text-[11px]">Winkelwagen</span>
            </div>
            <button type="button" onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-[#1e2b22] sm:hidden" aria-label={isMenuOpen ? 'Sluit menu' : 'Open menu'}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        <nav className="hidden h-[56px] border-t border-[#e7e1d6] md:block">
          <div className="mx-auto flex h-full max-w-[1240px] items-center px-5 lg:px-8">
            {navItems.map(renderNavItem)}
            <a href="/blogs" className="relative flex h-full items-center px-5 text-[14px] text-[#1a1a1a] hover:text-[#1e2b22]">Inspiratie</a>
            <div
              className="relative h-full"
              onMouseEnter={() => setIsKlantenserviceOpen(true)}
              onMouseLeave={() => setIsKlantenserviceOpen(false)}
            >
              <a
                href="/klantenservice"
                className={`relative flex h-full items-center gap-1 px-5 text-[14px] tracking-[-0.01em] transition-colors ${currentPath.startsWith('/klantenservice') ? 'font-semibold text-[#1e2b22]' : 'text-[#1a1a1a] hover:text-[#1e2b22]'}`}
              >
                Klantenservice
                <ChevronDown className={`h-3.5 w-3.5 text-[#5a6552] transition-transform ${isKlantenserviceOpen ? 'rotate-180' : ''}`} strokeWidth={1.7} aria-hidden="true" />
                <span className={`absolute inset-x-5 bottom-0 h-[2px] origin-left bg-[#1e2b22] transition-transform ${currentPath.startsWith('/klantenservice') ? 'scale-x-100' : 'scale-x-0'}`} />
              </a>
              {isKlantenserviceOpen && klantenserviceItems.length > 0 && (
                <div className="absolute left-0 top-full w-64 border border-[#e7e1d6] bg-[#fcfaf6] py-2 shadow-[0_12px_32px_-8px_rgba(35,38,35,0.12)]">
                  {klantenserviceItems.map((p) => (
                    <a
                      key={p.slug}
                      href={`/klantenservice/${p.slug}`}
                      onClick={closeMenus}
                      className={`block px-5 py-2.5 text-[13px] transition-colors hover:bg-[#f2ede4] ${currentPath === `/klantenservice/${p.slug}` ? 'font-medium text-[#1e2b22]' : 'text-[#1a1a1a]'}`}
                    >
                      {p.title}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </nav>

        <div className="mx-auto max-w-[1240px] px-5 pb-3 md:hidden">
          <a href="/reviews" className="mb-2 flex flex-col items-center gap-0.5" aria-label={`Beoordeeld met een ${reviewScore.toFixed(1).replace('.', ',')} / 5 — bekijk reviews`}>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`h-3.5 w-3.5 ${s <= Math.round(reviewScore) ? 'fill-amber-400 text-amber-400' : 'fill-[#e7e1d6] text-[#e7e1d6]'}`} />
              ))}
            </div>
            <span className="text-[11px] font-medium leading-tight text-[#5a6552]">Beoordeeld met een {reviewScore.toFixed(1).replace('.', ',')}</span>
          </a>
          <SearchBar variant="mobile" />
        </div>

        {isMenuOpen && (
          <div className="border-t border-[#e7e1d6] bg-[#fcfaf6] px-5 py-4 md:hidden">
            {[...navItems, { name: 'Inspiratie', href: '/blogs', categorySlug: null }].map((item) => (
              <a key={item.name} href={item.href} onClick={closeMenus} className="block border-b border-[#e7e1d6] py-4 text-[15px] text-[#1a1a1a]">{item.name}</a>
            ))}
            <a href="/zakelijk/inloggen" onClick={closeMenus} className="block border-b border-[#e7e1d6] py-4 text-[15px] text-[#1a1a1a] flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Zakelijk inloggen
            </a>
            <div className="border-b border-[#e7e1d6] py-4">
              <div className="mb-2 text-[13px] font-medium text-[#5a6552]">Klantenservice</div>
              <div className="flex flex-col gap-1">
                {klantenserviceItems.map((p) => (
                  <a key={p.slug} href={`/klantenservice/${p.slug}`} onClick={closeMenus} className="block py-1.5 text-[15px] text-[#1a1a1a] hover:text-[#1e2b22]">{p.title}</a>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Spacer so all page content starts below the fixed navbar */}
      <div className="h-[210px] w-full md:h-[176px]" aria-hidden="true" />

      <div className="fixed inset-x-0 top-[210px] z-40 md:top-[176px] hidden md:block" onMouseEnter={() => setIsMegaMenuOpen(true)} onMouseLeave={() => setIsMegaMenuOpen(false)}>
        <AssortimentMegaMenu isOpen={isMegaMenuOpen} activeCategory={activeCategory} />
      </div>

      <FavoritesDrawer open={isFavoritesOpen} onClose={() => setIsFavoritesOpen(false)} />
    </>
  );
};

export default Header;
